import { createAsyncThunk } from '@reduxjs/toolkit';
import { pushToast } from '../reportingSlice';
import { DateTime } from 'luxon';
import { ReportConfig } from 'src/types/reportingTypes';
import FileDownload from 'js-file-download';
import { RootState } from 'src/redux/store';

export type ExportReportAsCSVResult = {
	accountId?: string;
	name?: string;
	config?: ReportConfig;
	zoneId?: string;
};

export const exportReportAsCSV = createAsyncThunk<
	void,
	ExportReportAsCSVPayload,
	{ rejectValue: string; state: RootState }
>(
	'reporting/exportReportAsCSV',
	async (payload, { dispatch, rejectWithValue, getState }) => {
		const { api, accountId, config, zoneId, name } = payload;
		const { absoluteDateRangeStart, absoluteDateRangeEnd } = config;
		const state = getState();
		const activeReportName = state.reporting.activeReport.name;

		const formattedStartDate = absoluteDateRangeStart
			? DateTime.fromISO(absoluteDateRangeStart, { setZone: true })
					.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS")
			: null;

		const formattedEndDate = absoluteDateRangeEnd
			? DateTime.fromISO(absoluteDateRangeEnd, { setZone: true })
					.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS")
			: null;

		const updatedChartConfig = {
			...config,
			absoluteDateRangeStart: formattedStartDate,
			absoluteDateRangeEnd: formattedEndDate,
			groupBy: config.groupBy || [],
			sortBy: config.sortBy || [],
			filters: config.filters || [],
			topDisplayCount: config.topDisplayCount || 5,
			reportName: activeReportName,
		};
		const safeName = name && name.trim() ? name : 'report';
		try {
			const result = await api.exportReportAsCSV(
				accountId,
				updatedChartConfig,
				zoneId,
			);
			FileDownload(result, `${safeName}.csv`);
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: 'An unexpected error occurred';
			dispatch(
				pushToast({
					type: 'error',
					message: 'Error exporting report. Please try again later.',
				}),
			);
			return rejectWithValue(message);
		}
	},
);

type ExportReportAsCSVPayload = {
	api: {
		exportReportAsCSV: (
			accountId: string,
			config: ReportConfig,
			zoneId: string,
		) => Promise<string>;
	};
	accountId: string;
	config: ReportConfig;
	zoneId: string;
	name: string;
};
